import { Flex } from "@/components/atoms/flex";
import {
	MenuItem,
	MenuList,
	OnOpenChangeData,
	OpenPopoverEvents,
	mergeClasses,
	tokens,
} from "@fluentui/react-components";
import {
	Pause20Regular,
	CallPark20Regular,
	CallEnd20Filled,
	Play20Regular,
	HourglassHalf20Regular,
	PeopleCommunity20Regular,
	PersonVoice24Regular,
	ArrowForward20Regular,
	RecordRegular,
	RecordStopRegular,
	SlideTextCallRegular,
	MicRegular,
	MicOffRegular,
	MicProhibitedRegular,
} from "@fluentui/react-icons";
import { SquareToggleButton } from "@/components/atoms/squareToggleButton";
import { useIntl } from "react-intl";
import { ColdTransferToQueue20Regular } from "@/common/icons";
import { PopoverView } from "@/components/atoms/popover";
import { InteractionTransferToAgentPopover } from "@/components/organisms/InteractionTransferToAgent";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import {
	InteractionActionPopoverType,
	InteractionPopoverType,
	TransferChannelType,
	TransferType,
} from "@/common/types/transfer";
import { CallWarmTransferInProgressActionButtons } from "../callWarmTransferInProgressActionButtons/CallWarmTransferInProgressActionButtons";
import {
	DEFAULT_TRANSCRIPTION_LANGUAGE,
	deleteCall,
	pauseCall,
	resumeCall,
	parkCall,
	startTranscriptionCall,
	stopTranscriptionCall,
	checkCanGenerateSummary,
	CanGenerateConversationSummary,
	startRecordingCall,
	stopRecordingCall,
	fetchQueueTranscriptionLanguage,
} from "@/repository/queryFns";
import { useCallback, useMemo, useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { notificationListener } from "@/hooks/notification";
import { ServerEvent } from "@/common/notification";
import {
	QueuesState,
	useQueuesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { InteractionTransferIvrPopover } from "@/components/organisms/interactionTransferToIvr/InteractionTransferIvrPopover";
import {
	AppCacheActions,
	AppCacheState,
	AppSettingsState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import {
	CallSessionParticipantType,
	CallType,
} from "@/common/models/callSession";
import {
	switchMonitoring,
	stopMonitoring,
} from "@/repository/queryFns/supervisorInteractions";
import {
	SuperviseCallDto,
	MonitorTypes,
	EjectParticipantRequest,
} from "@/repository/models/monitoring";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import { NotesButton } from "@/components/atoms/notes";
import { VoiceCallSummaryButton } from "@/components/atoms/voiceCallSummary";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import { useInteractionBarCommonStyle } from "@/common/style/interactionBarCommonStyle";
import { useAcs } from "@/common/acs/AcsContext";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { PlatformType } from "@/common/constants/shared";
import {
	CurrentInteractionActions,
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { useIsAicLockedForInteraction } from "@/hooks/aicLocking";
import { OngoingCall } from "@/repository/models/outboundCall";
import { InteractionDirection } from "@/repository/models";
import { OnHoldStatusChanged } from "@/common/models/onHoldStatus";
import { useMessage } from "@/common/messages";

const ongoingCallStates: AgentInteractionStatus[] = [
	AgentInteractionStatus.Active,
	AgentInteractionStatus.WarmTransferInviteRejected,
	AgentInteractionStatus.WarmCanceled,
	AgentInteractionStatus.WarmCanceledBySecondAgent,
	AgentInteractionStatus.CalleeUnavailable,
	AgentInteractionStatus.Paused,
	AgentInteractionStatus.Resumed,
];

const callStatesThatDisallowEndButton = [
	AgentInteractionStatus.Terminated,
	AgentInteractionStatus.WarmTransferInviteSent,
	AgentInteractionStatus.WarmTransferInviteAccepted,
	AgentInteractionStatus.WarmTransferConsultingCaller,
	AgentInteractionStatus.WarmTransferConsultingAgent,
	AgentInteractionStatus.InitiatedWarmTransferConferenceMode,
];

const resolveTransferTooltipId = (
	isVideoCallActive: boolean,
	isTransferAllowed: boolean,
	enabledId: string,
	disabledId: string
): string => {
	if (isVideoCallActive) {
		return "Interaction.Buttons.TransferDisabledDuringVideoCall";
	}

	return isTransferAllowed ? enabledId : disabledId;
};

export const CallInProgressActionButtons = () => {
	const style = useInteractionBarCommonStyle();
	const intl = useIntl();
	const [pauseActionInProgress, setPauseActionInProgress] =
		useState<boolean>(false);
	const [transcriptionActionInProgress, setTranscriptionActionInProgress] =
		useState<boolean>(false);
	const [recordingActionInProgress, setRecordingActionInProgress] =
		useState<boolean>(false);
	const [waitingForRecordingStartedEvent, setWaitingForRecordingStartedEvent] =
		useState<boolean>(false);
	const [
		waitingForTranscriptionStartedEvent,
		setWaitingForTranscriptionStartedEvent,
	] = useState<boolean>(false);
	const [isMuted, setIsMuted] = useState<boolean>(false);
	const queues = useQueuesStore((state: QueuesState) => state.queues);
	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const [interactionPopoverType, setInteractionPopoverType] =
		useState<InteractionPopoverType | null>(null);

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const currentInteractionActions = useCurrentInteractionStore(
		(actions: CurrentInteractionActions) => actions
	);

	// An agent-initiated webchat video call must stay between this agent and the web customer:
	// transferring it (to agent or queue/flow) is not allowed, and neither is putting it on hold —
	// the customer's widget has no hold state to show, so they would sit in a silent call with no
	// idea why. Declared here because the pause button below is built before the transfer gates.
	const isWebchatVideoCall = !!callSession?.webchatConversationId;

	const [isPauseButtonVisible, setIsPauseButtonVisible] =
		useState<boolean>(false);

	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);

	const isAicLocked = useIsAicLockedForInteraction(callSession?.firstAgentAssignedAt);

	const {
		ongoingCalls,
		muteCall,
		unmuteCall,
		muteAllRemoteParticipants,
		hangUpCall,
		holdCall,
		unholdCall,
	} = useAcs();

	const [currentOngoingCall, setCurrentOngoingCall] =
		useState<OngoingCall | null>(null);

	const { show: showMessage } = useMessage();

	notificationListener(
		ServerEvent.OnHoldStatusChanged,
		(onHoldChangedData: OnHoldStatusChanged) => {
			if (onHoldChangedData.IsHoldFailed) {
				showMessage("workspace", {
					intent: "warning",
					text: intl.formatMessage({ id: "ErrorMessage.Hold" }),
				});
				setOnHoldStatus(false);
				setPauseActionInProgress(false);
				return;
			}
			if (onHoldChangedData.Status) {
				showMessage("workspace", {
					intent: "warning",
					text: intl.formatMessage({ id: "Interaction.CallOnHold" }),
				});
			}
			if (onHoldChangedData.IsStatusDifferentOnClient) {
				setOnHoldStatus(onHoldChangedData.Status);
			}
			setPauseActionInProgress(false);
		}
	);

	useEffect(() => {
		if (callSession?.state === AgentInteractionStatus.CalleeUnavailable) {
			setOnHoldStatus(true);
		} else {
			setOnHoldStatus(appStoreCache[CacheKey.OnHoldStatus]);
		}

		setTranscriptionActionInProgress(false);
		setRecordingActionInProgress(false);
	}, [callSession]);

	useEffect(() => {
		if (callSession?.recordingInProgress) {
			setWaitingForRecordingStartedEvent(false);
		}
	}, [callSession?.recordingInProgress]);

	useEffect(() => {
		if (callSession?.transcriptionInProgress) {
			setWaitingForTranscriptionStartedEvent(false);
		}
	}, [callSession?.transcriptionInProgress]);

	useEffect(() => {
		setWaitingForRecordingStartedEvent(false);
		setWaitingForTranscriptionStartedEvent(false);
	}, [callSession?.id]);

	useEffect(() => {
		const ongoingCall = ongoingCalls?.get(currentInteraction?.id ?? "");
		if (!ongoingCall) {
			return () => {};
		}

		const handler = () => {
			setIsMuted(ongoingCall.call.isMuted);
		};

		ongoingCall.call.on("isMutedChanged", handler);

		setCurrentOngoingCall(ongoingCall);
		setIsMuted(ongoingCall.call.isMuted);

		return () => ongoingCall.call.off("isMutedChanged", handler);
	}, [ongoingCalls, currentInteraction?.id]);

	const toggleInteractionPopoverOpening = useCallback(
		(type: InteractionPopoverType) => {
			setInteractionPopoverType((oldState) =>
				oldState === type ? null : type
			);
		},
		[setInteractionPopoverType]
	);

	const setOnHoldStatus = (onHoldStatus: boolean) => {
		setIsPauseButtonVisible(!onHoldStatus);
		appStoreActions.setCache(CacheKey.OnHoldStatus, onHoldStatus);
	};

	const endCallMutation = useMutation({
		mutationFn: deleteCall,
		onError: (error: any, data: any, _context: any) => {
			captureError(error, "Error ending call", undefined, data);
			console.error(error);
		},
	});

	const pauseCallMutation = useMutation({
		mutationFn: pauseCall,
		onError: (error: any, data: any, _context: any) => {
			captureError(error, "Error pausing call", undefined, data);
			console.error(error);
		},
	});

	const resumeCallMutation = useMutation({
		mutationFn: resumeCall,
		onError: (error: any, data: any, _context: any) => {
			captureError(error, "Error resuming call", undefined, data);
			console.error(error);
		},
	});

	const parkCallMutation = useMutation({
		mutationFn: parkCall,
		onSuccess: () => {
			showMessage("workspace", {
				intent: "success",
				text: intl.formatMessage({ id: "Interaction.CallParked" }),
			});
		},
		onError: (error: any, data: any, _context: any) => {
			captureError(error, "Error parking call", undefined, data);
			console.error(error);
		},
	});

	const startCallTranscriptionMutation = useMutation({
		mutationFn: startTranscriptionCall,
		onError: (error: any, data: any, _context: any) => {
			setWaitingForTranscriptionStartedEvent(false);
			captureError(error, "Error starting transcription call", undefined, data);
			console.error(error);
		},
	});

	const stopCallTranscriptionMutation = useMutation({
		mutationFn: stopTranscriptionCall,
		onError: (error: any, data: any, _context: any) => {
			captureError(error, "Error stopping transcription", undefined, data);
			console.error(error);
		},
	});

	const startCallRecordingMutation = useMutation({
		mutationFn: startRecordingCall,
		onError: (error: any, data: any, _context: any) => {
			setWaitingForRecordingStartedEvent(false);
			captureError(error, "Error starting recording call", undefined, data);
			console.error(error);
		},
	});

	const stopCallRecordingMutation = useMutation({
		mutationFn: stopRecordingCall,
		onError: (error: any, data: any, _context: any) => {
			captureError(error, "Error stopping recording", undefined, data);
			console.error(error);
		},
	});

	const endCall = useCallback(() => {
		addBreadcrumb("ui_interaction", "Ending call", callSession);
		hangUpCall(currentInteraction?.id ?? "");

		if (!callSession) {
			currentInteractionActions.hideActive();

			return;
		}

		endCallMutation.mutate(undefined);
	}, [endCallMutation]);

	const endMonitoringCall = useCallback(() => {
		addBreadcrumb("ui_interaction", "Ending monitoring call", callSession);
		const ejectParticipantRequest = {
			scenarioId: callSession?.id,
			participantId: Lockr.get(StorageKeys.UserId),
			tenantId: Lockr.get(StorageKeys.TenantId),
		} as EjectParticipantRequest;

		stopMonitoring(ejectParticipantRequest);
	}, [callSession]);

	const pauseCallAction = useCallback(() => {
		addBreadcrumb("ui_interaction", "Pause call", callSession);
		setOnHoldStatus(true);

		if (callSession) {
			setPauseActionInProgress(true);
			pauseCallMutation.mutate(undefined);
		} else {
			holdCall(currentInteraction?.id ?? "");
		}
	}, [pauseCallMutation, callSession]);

	const resumeCallAction = useCallback(() => {
		addBreadcrumb("ui_interaction", "Resume call", callSession);
		setOnHoldStatus(false);

		if (callSession) {
			setPauseActionInProgress(true);
			resumeCallMutation.mutate(undefined);
		} else {
			unholdCall(currentInteraction?.id ?? "");
		}
	}, [resumeCallMutation, callSession]);

	const getPauseButtonByStatus = (onClick: any, isPause: boolean) => {
		const buttonIcon = isPause ? <Pause20Regular /> : <Play20Regular />;
		const state = isPause ? "Pause" : "Resume";

		return (
			<SquareToggleButton
				icon={pauseActionInProgress ? <HourglassHalf20Regular /> : buttonIcon}
				tooltip={intl.formatMessage({
					id: isWebchatVideoCall
						? "Interaction.Buttons.PauseDisabledDuringVideoCall"
						: `Interaction.Buttons.${state}`,
				})}
				onClick={onClick}
				disabled={pauseActionInProgress || isWebchatVideoCall}
			/>
		);
	};

	const pauseResumeButton = useMemo<JSX.Element>(() => {
		const action = isPauseButtonVisible ? pauseCallAction : resumeCallAction;

		return getPauseButtonByStatus(action, isPauseButtonVisible);
	}, [isPauseButtonVisible, pauseActionInProgress, isWebchatVideoCall]);

	const parkCallAction = useCallback(() => {
		addBreadcrumb("ui_interaction", "Park call", callSession);
		parkCallMutation.mutate(undefined);
	}, [parkCallMutation]);

	const parkButton = useMemo<any>(() => {
		const agentHasParkingRightOnQueue = queues.find(
			(queueUser: any) =>
				queueUser.id === callSession?.queueId &&
				queueUser.isOperator &&
				queueUser.hasParkingRight
		);

		return agentHasParkingRightOnQueue &&
			callSession &&
			callSession.callType === CallType.Incoming &&
			!callSession.wasCallTransferred ? (
			<SquareToggleButton
				icon={<CallPark20Regular />}
				tooltip={intl.formatMessage({ id: "Interaction.Buttons.Park" })}
				onClick={parkCallAction}
				disabled={waitingForRecordingStartedEvent}
			/>
		) : undefined;
	}, [
		callSession,
		queues,
		parkCallAction,
		waitingForRecordingStartedEvent,
	]);

	const isOngoingCall =
		!!callSession?.state &&
		ongoingCallStates.includes(callSession?.state) &&
		callSession.currentAgent?.type === CallSessionParticipantType.PrimaryAgent;

	const isMonitoringCall = useMemo(() => {
		const isMonitoring =
			!!callSession?.state &&
			callSession.currentAgent?.type === CallSessionParticipantType.Supervisor;

		return isMonitoring;
	}, [callSession]);

	const hasNoteButton = useMemo(() => {
		return (
			isOngoingCall ||
			isMonitoringCall ||
			callSession?.state == AgentInteractionStatus.WarmTransferInviteAccepted ||
			callSession?.state ==
				AgentInteractionStatus.WarmTransferConsultingCaller ||
			callSession?.state ==
				AgentInteractionStatus.WarmTransferConsultingAgent ||
			callSession?.state ==
				AgentInteractionStatus.AcceptedWarmTransferInvitation ||
			callSession?.state ==
				AgentInteractionStatus.InitiatedWarmTransferConferenceMode ||
			callSession?.state ==
				AgentInteractionStatus.PartOfWarmTransferConferenceMode
		);
	}, [callSession]);

	const { data: canGenerateSummary } = useQuery<boolean>({
		queryKey: [CanGenerateConversationSummary],
		queryFn: (_: any) => checkCanGenerateSummary(),
		refetchOnMount: "always",
	});

	const isAcsCall = useMemo(() => {
		return (
			callSession?.platformType == PlatformType.Acs &&
			hasNoteButton
		);
	}, [callSession, hasNoteButton]);

	const isUnifyCall = useMemo(() => {
		return isAcsCall && canGenerateSummary === true;
	}, [isAcsCall, canGenerateSummary]);

	const currentQueue = useMemo(
		() => queues.find((q) => q.id === callSession?.queueId),
		[queues, callSession?.queueId]
	);

	const isTranscriptionFeatureUnavailable =
		!(currentQueue?.enableTranscription ?? false);
	const isTranscriptionAutoStarted =
		currentQueue?.autoStartTranscription && callSession?.transcriptionInProgress;
	const isTranscriptionDisabled =
		isTranscriptionFeatureUnavailable ||
		isTranscriptionAutoStarted ||
		isAicLocked;

	const isRecordingFeatureUnavailable =
		!(currentQueue?.enableRecording ?? false);
	const isRecordingAutoStarted = currentQueue?.autoStartRecording && callSession?.recordingInProgress;
	const isRecordingDisabled =
		isRecordingFeatureUnavailable || isRecordingAutoStarted || isAicLocked;

	const switchMonitoringByType = useCallback((type: MonitorTypes) => {
		const userId = Lockr.get(StorageKeys.UserId);

		const monitoringData = {
			scenarioId: callSession?.id,
			supervisingAgentId: userId,
			monitoringType: type,
		} as SuperviseCallDto;

		switchMonitoring(monitoringData);
	}, []);

	const bargeIn = useCallback(() => {
		if (currentOngoingCall) {
			unmuteCall(currentInteraction?.id ?? "");
			switchMonitoringByType(MonitorTypes.BargeIn);
		}
	}, [currentOngoingCall]);

	const isWarmTransferInProgress = useMemo((): boolean => {
		if (callSession?.state === undefined) {
			return false;
		}
		return [
			AgentInteractionStatus.WarmTransferInviteAccepted,
			AgentInteractionStatus.WarmTransferConsultingAgent,
			AgentInteractionStatus.WarmTransferConsultingCaller,
			AgentInteractionStatus.InitiatedWarmTransferConferenceMode,
		].includes(callSession.state);
	}, [callSession?.state]);

	const isWarmTransferStarted = useMemo(
		(): boolean =>
			callSession?.state === AgentInteractionStatus.WarmTransferInviteSent ||
			callSession?.state ===
				AgentInteractionStatus.AcceptedWarmTransferInvitation,
		[callSession?.state]
	);

	const isEndButtonVisible = () => {
		return (
			isOngoingCall ||
			isMonitoringCall ||
			(isWarmTransferInProgress && isUnifyCall) ||
			(!callSession && currentOngoingCall) ||
			(callSession?.callType === CallType.Outgoing &&
				callSession.currentAgent?.type ===
					CallSessionParticipantType.PrimaryAgent &&
				callSession?.state != null &&
				!callStatesThatDisallowEndButton.includes(callSession.state)) ||
			callSession?.currentAgent?.type === CallSessionParticipantType.Caller
		);
	};

	const handleOpenChange = (_e: OpenPopoverEvents, data: OnOpenChangeData) => {
		if (!data.open) {
			setInteractionPopoverType(null);
		}
	};

	const isTransferEnabled =
		(appSettings.callColdTransferEnabled ||
			appSettings.callWarmTransferEnabled ||
			appSettings.routeCallEnabled) &&
		!isWebchatVideoCall;
	const isTransferIVREnabled =
		(appSettings.callColdTransferIVREnabled ||
			appSettings.callWarmTransferIVREnabled) &&
		!isWebchatVideoCall;

	const startTranscriptionAction = useCallback(async () => {
		addBreadcrumb("ui_interaction", "Start transcription", callSession);
		setTranscriptionActionInProgress(true);
		setWaitingForTranscriptionStartedEvent(true);
		// Reuse the language the agent already selected for this interaction;
		// only fall back to the queue default when none has been set yet.
		let language = callSession?.transcriptionLanguage;
		if (!language && callSession?.queueId) {
			try {
				language = await fetchQueueTranscriptionLanguage(callSession.queueId);
			} catch (error) {
				captureError(
					error instanceof Error ? error : String(error),
					"Error fetching queue transcription language"
				);
				console.error(error);
			}
		}
		startCallTranscriptionMutation.mutate(
			language ?? DEFAULT_TRANSCRIPTION_LANGUAGE
		);
	}, [callSession, startCallTranscriptionMutation]);

	const stopTranscriptionAction = useCallback(() => {
		addBreadcrumb("ui_interaction", "Stop transcription", callSession);
		setTranscriptionActionInProgress(true);
		if (callSession) {
			callSession.transcriptionInProgress = false;
		}
		stopCallTranscriptionMutation.mutate(undefined);
	}, [stopCallTranscriptionMutation]);

	const startRecordingAction = useCallback(() => {
		addBreadcrumb("ui_interaction", "Start recording", callSession);
		setRecordingActionInProgress(true);
		setWaitingForRecordingStartedEvent(true);
		startCallRecordingMutation.mutate(undefined);
	}, [startCallRecordingMutation]);

	const stopRecordingAction = useCallback(() => {
		addBreadcrumb("ui_interaction", "Stop recording", callSession);
		setRecordingActionInProgress(true);
		if (callSession) {
			callSession.recordingInProgress = false;
		}
		stopCallRecordingMutation.mutate(undefined);
	}, [stopCallRecordingMutation]);

	const startMuteAction = useCallback(() => {
		addBreadcrumb("ui_interaction", "Start mute", callSession);
		muteCall(currentInteraction?.id ?? "");
		setInteractionPopoverType(null);
	}, []);

	const stopMuteAction = useCallback(() => {
		addBreadcrumb("ui_interaction", "Stop mute", callSession);
		unmuteCall(currentInteraction?.id ?? "");
		setInteractionPopoverType(null);
	}, []);

	const startMuteOthersAction = useCallback(() => {
		addBreadcrumb("ui_interaction", "Start mute others", callSession);
		muteAllRemoteParticipants(currentInteraction?.id ?? "");
		setInteractionPopoverType(null);
	}, []);

	const getTranscriptionButtonByStatus = (
		onClick: any,
		disabled = false,
		featureUnavailable = false
	) => {
		const transcriptionInProgress =
			!isAicLocked && callSession?.transcriptionInProgress;
		const state = transcriptionInProgress ? "Stop" : "Start";

		return (
			<SquareToggleButton
				icon={
					transcriptionActionInProgress ? (
						<HourglassHalf20Regular />
					) : (
						<SlideTextCallRegular />
					)
				}
				tooltip={
					featureUnavailable && !transcriptionInProgress
						? intl.formatMessage({
								id: "Interaction.Buttons.FeatureNotAvailable",
							})
						: intl.formatMessage({
								id: `Interaction.Buttons.Transcription${state}`,
							})
				}
				onClick={onClick}
				disabled={transcriptionActionInProgress || disabled}
				checked={transcriptionInProgress}
			/>
		);
	};

	const getRecordingButtonByStatus = (
		onClick: any,
		disabled = false,
		featureUnavailable = false
	) => {
		const recordingInProgress =
			!isAicLocked && callSession?.recordingInProgress;
		const buttonIcon = recordingInProgress ? (
			<RecordStopRegular className={style.iconRecordStop} />
		) : (
			<RecordRegular />
		);
		const state = recordingInProgress ? "Stop" : "Start";

		return (
			<SquareToggleButton
				icon={
					recordingActionInProgress ? <HourglassHalf20Regular /> : buttonIcon
				}
				tooltip={
					featureUnavailable && !recordingInProgress
						? intl.formatMessage({
								id: "Interaction.Buttons.FeatureNotAvailable",
							})
						: intl.formatMessage({
								id: `Interaction.Buttons.Recording${state}`,
							})
				}
				onClick={onClick}
				disabled={recordingActionInProgress || disabled}
				checked={recordingInProgress}
			/>
		);
	};

	const toggleTranscriptionButton = useMemo<JSX.Element>(() => {
		const action = callSession?.transcriptionInProgress
			? stopTranscriptionAction
			: startTranscriptionAction;

		return getTranscriptionButtonByStatus(
			action,
			isTranscriptionDisabled || waitingForTranscriptionStartedEvent,
			isTranscriptionFeatureUnavailable || isAicLocked
		);
	}, [
		callSession?.transcriptionInProgress,
		transcriptionActionInProgress,
		waitingForTranscriptionStartedEvent,
		isTranscriptionDisabled,
		isTranscriptionFeatureUnavailable,
		isAicLocked,
	]);

	const toggleRecordingButton = useMemo<JSX.Element>(() => {
		const action = callSession?.recordingInProgress
			? stopRecordingAction
			: startRecordingAction;

		return getRecordingButtonByStatus(
			action,
			isRecordingDisabled || waitingForRecordingStartedEvent,
			isRecordingFeatureUnavailable || isAicLocked
		);
	}, [
		callSession?.recordingInProgress,
		recordingActionInProgress,
		waitingForRecordingStartedEvent,
		isRecordingDisabled,
		isRecordingFeatureUnavailable,
		isAicLocked,
	]);

	const toggleMuteButton = useMemo<JSX.Element>(() => {
		const action = isMuted ? stopMuteAction : startMuteAction;
		const buttonIcon = isMuted ? <MicOffRegular /> : <MicRegular />;
		const state = isMuted ? "Stop" : "Start";

		return (
			<PopoverView
				trigger={
					<SquareToggleButton
						icon={buttonIcon}
						tooltip={intl.formatMessage({
							id: `Interaction.Buttons.Mute${state}`,
						})}
						onClick={() =>
							toggleInteractionPopoverOpening(InteractionActionPopoverType.Mute)
						}
						checked={
							interactionPopoverType === InteractionActionPopoverType.Mute
						}
					/>
				}
				content={
					<MenuList>
						<MenuItem icon={buttonIcon} onClick={action}>
							{intl.formatMessage({
								id: `Interaction.Buttons.Mute${state}`,
							})}
						</MenuItem>
						<MenuItem
							icon={<MicProhibitedRegular />}
							onClick={startMuteOthersAction}
						>
							{intl.formatMessage({
								id: `Interaction.Buttons.MuteOthers`,
							})}
						</MenuItem>
					</MenuList>
				}
				positioning={{
					position: "below",
					align: "end",
					offset: { mainAxis: 8 },
				}}
				open={interactionPopoverType === InteractionActionPopoverType.Mute}
				onOpenChange={handleOpenChange}
			/>
		);
	}, [isMuted, interactionPopoverType]);

	const hasBasicCallActions =
		isOngoingCall ||
		(currentOngoingCall &&
			currentOngoingCall.direction === InteractionDirection.Incoming &&
			!currentOngoingCall.isCCaaSCall);

	const muteUnmuteVisible =
		currentOngoingCall && (hasBasicCallActions || isWarmTransferInProgress);

	return (
		<Flex
			vAlign="center"
			className={mergeClasses(style.interactionText, style.interactionButtons)}
		>
			{muteUnmuteVisible && toggleMuteButton}
			{hasBasicCallActions && pauseResumeButton}
			{isOngoingCall && (
				<>
					{isAcsCall && toggleTranscriptionButton}
					{isAcsCall && toggleRecordingButton}
					{parkButton}
					<PopoverView
						trigger={
							<SquareToggleButton
								icon={<ArrowForward20Regular />}
								tooltip={intl.formatMessage({
									id: resolveTransferTooltipId(
										isWebchatVideoCall,
										isTransferEnabled,
										"Interaction.Buttons.TransferToAgent",
										"Interaction.Buttons.TransferToAgentDisabled"
									),
								})}
								onClick={() =>
									toggleInteractionPopoverOpening(TransferType.AgentTransfer)
								}
								checked={interactionPopoverType === TransferType.AgentTransfer}
								disabled={!isTransferEnabled}
							/>
						}
						content={
							<InteractionTransferToAgentPopover
								channelType={TransferChannelType.Voice}
								closePopover={() =>
									toggleInteractionPopoverOpening(TransferType.AgentTransfer)
								}
								isInteractionMonitored={!!callSession.supervisorId}
							/>
						}
						positioning={{
							position: "below",
							align: "end",
							offset: { mainAxis: 8 },
						}}
						open={interactionPopoverType === TransferType.AgentTransfer}
						onOpenChange={handleOpenChange}
					/>
					<PopoverView
						trigger={
							<SquareToggleButton
								icon={<ColdTransferToQueue20Regular />}
								tooltip={intl.formatMessage({
									id: resolveTransferTooltipId(
										isWebchatVideoCall,
										isTransferIVREnabled,
										"Interaction.Buttons.TransferToIvr",
										"Interaction.Buttons.TransferToIvrDisabled"
									),
								})}
								onClick={() =>
									toggleInteractionPopoverOpening(TransferType.IvrTransfer)
								}
								checked={interactionPopoverType === TransferType.IvrTransfer}
								disabled={!isTransferIVREnabled}
							/>
						}
						content={
							<InteractionTransferIvrPopover
								closePopover={() =>
									toggleInteractionPopoverOpening(TransferType.IvrTransfer)
								}
								channelType={TransferChannelType.Voice}
							/>
						}
						positioning={{
							position: "below",
							align: "end",
							offset: { mainAxis: 8 },
						}}
						open={interactionPopoverType === TransferType.IvrTransfer}
						onOpenChange={handleOpenChange}
					/>
				</>
			)}
			{isMonitoringCall && (
				<>
					<SquareToggleButton
						icon={<PersonVoice24Regular />}
						tooltip={intl.formatMessage({ id: "Interaction.Buttons.Listen" })}
						onClick={() => {
							switchMonitoringByType(MonitorTypes.Silent);
						}}
						disabled={
							endCallMutation.isPending ||
							callSession?.monitorType === MonitorTypes.Silent
						}
					/>
					<SquareToggleButton
						icon={<PeopleCommunity20Regular />}
						tooltip={intl.formatMessage({
							id: `Interaction.Buttons.${callSession?.monitorType === MonitorTypes.BargeIn ? "BargeIn" : "Unmute"}`,
						})}
						onClick={() => {
							bargeIn();
						}}
						disabled={
							endCallMutation.isPending ||
							callSession?.monitorType === MonitorTypes.BargeIn
						}
					/>
				</>
			)}
			{hasNoteButton && (
				<NotesButton
					interactionId={callSession?.id!}
					callId={callSession?.callId!}
					editMode={true}
					toggleInteractionPopoverOpening={toggleInteractionPopoverOpening}
					interactionPopoverType={interactionPopoverType}
					onOpenChange={handleOpenChange}
					canGenerateSummary={
						canGenerateSummary === true
					}
					enableCallSummary={currentQueue?.enableCallSummary ?? false}
				/>
			)}
			{isAcsCall && (
				<VoiceCallSummaryButton
					interactionId={callSession?.id}
					interactionStartDate={callSession?.startDate}
					firstAgentAssignedAt={callSession?.firstAgentAssignedAt}
					canGenerateSummary={canGenerateSummary === true}
					enableCallSummary={currentQueue?.enableCallSummary ?? false}
				/>
			)}
			<CallWarmTransferInProgressActionButtons
				isWarmTransferInProgress={isWarmTransferInProgress}
				isWarmTransferStarted={isWarmTransferStarted}
			/>
			{isEndButtonVisible() && (
				<SquareToggleButton
					icon={
						<CallEnd20Filled
							primaryFill={
								isUnifyCall
									? tokens.colorPaletteRedBackground3
									: tokens.colorNeutralForegroundStaticInverted
							}
						/>
					}
					tooltip={intl.formatMessage({ id: "Interaction.Buttons.EndCall" })}
					className={
						isUnifyCall
							? style.squareRedIconButton
							: mergeClasses(style.squareColoredButton, style.redColor)
					}
					onClick={isMonitoringCall ? endMonitoringCall : endCall}
					disabled={endCallMutation.isPending}
				/>
			)}
		</Flex>
	);
};
